import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useBeforeUnload } from 'react-router-dom';
import { useMediaQuery, useTheme } from '@mui/material';
import { useAuth } from '../../../context/AuthContext';
import { useNotification } from '../../../context/NotificationContext';
import { patchCompany } from '../../../api/company/company';
import { objectFileName, uploadFile } from '../../../api/files';
import { buildSharedSlugUpdate, getCompanySlug } from '../../../client-experience/utils/companyAccess';
import { ensurePortalSlugCanBeSaved } from '../../../client-experience/utils/companyUtils';
import {
  getBusinessAddress,
  getBusinessEmail,
  getBusinessPhone
} from '../../../customer-landing/utils/contactInfo';
import {
  DEFAULT_SERVICE_LISTING_STYLE,
  DEFAULT_THEME_COLOR,
  SERVICE_LISTING_STYLE_OPTIONS
} from '../../../customer-landing/utils/landingPageTheme';
import {
  createLandingContactPage,
  createLandingCustomPage,
  LANDING_PAGE_SOCIAL_PLATFORMS,
  LANDING_PAGE_TYPES,
  normalizeLandingPageSettings,
  normalizeLandingPageSlug,
  sanitizeLandingPageSettingsForSave
} from '../../../customer-landing/utils/custom-pages/pageConfig';
import StorefrontBuilderPreview from './client-experience/storefront-preview/StorefrontBuilderPreview';
import './client-experience/styles/ClientExperience.css';

const HEX_COLOR_REGEX = /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/;
const THEME_COLOR_OPTIONS = [
  { label: 'Blue', value: '#1d4ed8' },
  { label: 'Navy', value: '#4f46e5' },
  { label: 'Purple', value: '#7c3aed' },
  { label: 'Teal', value: '#0f766e' },
  { label: 'Green', value: '#15803d' },
  { label: 'Orange', value: '#d97706' },
  { label: 'Red', value: '#e11d48' },
  { label: 'Gray', value: '#334155' }
];

const pageTypeLabels = {
  [LANDING_PAGE_TYPES.CONTACT]: 'Contact page',
  [LANDING_PAGE_TYPES.CUSTOM]: 'Standard page'
};

const buildStorefrontPreviewUrl = (slug, pageId = 'home', pages = []) => {
  const normalizedSlug = (slug || '').trim();
  if (!normalizedSlug) {
    return '';
  }

  const baseUrl = `${window.location.origin}/book/${encodeURIComponent(normalizedSlug)}`;
  if (pageId === 'home') {
    return baseUrl;
  }

  const page = pages.find((entry) => entry.id === pageId);
  if (!page?.slug) {
    return baseUrl;
  }

  return `${baseUrl}/${encodeURIComponent(page.slug)}`;
};

const LandingPageSettings = ({ embedded = false, onUnsavedChangesChange }) => {
  const theme = useTheme();
  const isMobilePreview = useMediaQuery(theme.breakpoints.down('md'));
  const { companyData, updateCompanyData } = useAuth();
  const { showToast } = useNotification();
  const [loading, setLoading] = useState(false);
  const [previewExpanded, setPreviewExpanded] = useState(true);
  const [loadedCompanyData, setLoadedCompanyData] = useState(companyData || null);
  const [colors, setColors] = useState({ theme: DEFAULT_THEME_COLOR });
  const [customUrl, setCustomUrl] = useState('');
  const [portalCustomUrl, setPortalCustomUrl] = useState('');
  const [serviceListingStyle, setServiceListingStyle] = useState(DEFAULT_SERVICE_LISTING_STYLE);
  const [storefrontBackgroundImageUrl, setStorefrontBackgroundImageUrl] = useState('');
  const [storefrontBackgroundOverlayOpacity, setStorefrontBackgroundOverlayOpacity] = useState(0.1);
  const [backgroundUploading, setBackgroundUploading] = useState(false);
  const [customThemeHex, setCustomThemeHex] = useState(DEFAULT_THEME_COLOR);
  const [navigationSettings, setNavigationSettings] = useState({
    showHome: true
  });
  const [socialLinks, setSocialLinks] = useState(() => LANDING_PAGE_SOCIAL_PLATFORMS.reduce((accumulator, platform) => ({
    ...accumulator,
    [platform.key]: ''
  }), {}));
  const [homePage, setHomePage] = useState({
    featuredSectionTitle: '',
    featuredSectionDescription: ''
  });
  const [customPages, setCustomPages] = useState([]);
  const [selectedPageId, setSelectedPageId] = useState('');
  const [previewPageId, setPreviewPageId] = useState('home');

  useEffect(() => {
    const loadSettings = async () => {
      if (!companyData?.id) {
        return;
      }

      try {
        const nextCompanyData = companyData;
        const normalizedSettings = normalizeLandingPageSettings(nextCompanyData.landingPageSettings, nextCompanyData);
        const sharedSlug = getCompanySlug(nextCompanyData);

        setLoadedCompanyData(nextCompanyData);
        setColors({ theme: normalizedSettings.themeColor });
        setCustomThemeHex(normalizedSettings.themeColor);
        setPortalCustomUrl(sharedSlug);
        setCustomUrl(sharedSlug);
        setServiceListingStyle(normalizedSettings.serviceListingStyle);
        setStorefrontBackgroundImageUrl(normalizedSettings.storefrontBackgroundImageUrl || '');
        setStorefrontBackgroundOverlayOpacity(normalizedSettings.storefrontBackgroundOverlayOpacity ?? 0.1);
        setNavigationSettings(normalizedSettings.navigation);
        setSocialLinks(normalizedSettings.socialLinks);
        setHomePage(normalizedSettings.homePage);
        setCustomPages(normalizedSettings.customPages);
        setSelectedPageId(normalizedSettings.customPages[0]?.id || '');
        setPreviewPageId('home');
      } catch (err) {
        console.error('Error loading landing page settings:', err);
        showToast('Failed to load settings', 'error');
      }
    };

    loadSettings();
  }, [companyData?.id]);

  const draftSettings = useMemo(() => sanitizeLandingPageSettingsForSave({
    themeColor: colors.theme,
    storefrontBackgroundImageUrl,
    storefrontBackgroundOverlayOpacity,
    serviceListingStyle,
    navigation: navigationSettings,
    socialLinks,
    homePage,
    customPages
  }, loadedCompanyData || companyData), [colors.theme, storefrontBackgroundImageUrl, storefrontBackgroundOverlayOpacity, serviceListingStyle, navigationSettings, socialLinks, homePage, customPages, loadedCompanyData, companyData]);

  const previewCompanyData = useMemo(() => ({
    ...(loadedCompanyData || companyData || {}),
    landingPageSettings: {
      ...draftSettings,
      customUrl: (portalCustomUrl || customUrl || '').trim()
    }
  }), [loadedCompanyData, companyData, draftSettings, portalCustomUrl, customUrl]);

  const previewPages = draftSettings.customPages;
  const selectedPage = customPages.find((page) => page.id === selectedPageId) || customPages[0] || null;
  const hasContactPage = previewPages.some((page) => page.type === LANDING_PAGE_TYPES.CONTACT);
  const previewAddress = getBusinessAddress(previewCompanyData);
  const previewEmail = getBusinessEmail(previewCompanyData);
  const previewPhone = getBusinessPhone(previewCompanyData);
  const resolvedSlug = (portalCustomUrl || customUrl || '').trim();
  const livePreviewUrl = useMemo(
    () => buildStorefrontPreviewUrl(resolvedSlug, previewPageId, previewPages),
    [resolvedSlug, previewPageId, previewPages]
  );
  const previewStyles = {
    '--ce-accent': colors.theme,
    '--ce-accent-soft': `${colors.theme}12`
  };
  const currentUrlRef = useRef(typeof window !== 'undefined'
    ? `${window.location.pathname}${window.location.search}${window.location.hash}`
    : '');
  const currentSnapshot = useMemo(() => JSON.stringify({
    slug: resolvedSlug,
    themeColor: colors.theme,
    storefrontBackgroundImageUrl,
    storefrontBackgroundOverlayOpacity,
    serviceListingStyle,
    navigation: navigationSettings,
    socialLinks,
    homePage,
    customPages: draftSettings.customPages
  }), [resolvedSlug, colors.theme, storefrontBackgroundImageUrl, storefrontBackgroundOverlayOpacity, serviceListingStyle, navigationSettings, socialLinks, homePage, draftSettings.customPages]);
  const loadedSnapshot = useMemo(() => {
    const normalizedLoadedSettings = normalizeLandingPageSettings(loadedCompanyData?.landingPageSettings, loadedCompanyData || companyData);

    return JSON.stringify({
      slug: getCompanySlug(loadedCompanyData || companyData),
      themeColor: normalizedLoadedSettings.themeColor,
      storefrontBackgroundImageUrl: normalizedLoadedSettings.storefrontBackgroundImageUrl,
      storefrontBackgroundOverlayOpacity: normalizedLoadedSettings.storefrontBackgroundOverlayOpacity,
      serviceListingStyle: normalizedLoadedSettings.serviceListingStyle,
      navigation: normalizedLoadedSettings.navigation,
      socialLinks: normalizedLoadedSettings.socialLinks,
      homePage: normalizedLoadedSettings.homePage,
      customPages: normalizedLoadedSettings.customPages
    });
  }, [loadedCompanyData, companyData]);
  const hasUnsavedChanges = currentSnapshot !== loadedSnapshot;

  useEffect(() => {
    if (typeof onUnsavedChangesChange === 'function') {
      onUnsavedChangesChange(hasUnsavedChanges);
    }
  }, [hasUnsavedChanges, onUnsavedChangesChange]);

  useEffect(() => {
    if (previewPageId !== 'home' && !previewPages.some((page) => page.id === previewPageId)) {
      setPreviewPageId('home');
    }
  }, [previewPageId, previewPages]);

  const handleThemeColorChange = (value) => {
    setCustomThemeHex(value);
    setColors({ theme: value });
  };

  const handleCustomThemeHexChange = (value) => {
    setCustomThemeHex(value);

    if (HEX_COLOR_REGEX.test(value.trim())) {
      setColors({ theme: value.trim() });
    }
  };

  const handleSocialLinkChange = (platformKey, value) => {
    setSocialLinks((currentLinks) => ({
      ...currentLinks,
      [platformKey]: value
    }));
  };

  const handleStorefrontBackgroundUpload = async (file) => {
    if (!file || !companyData?.id) {
      return;
    }

    try {
      setBackgroundUploading(true);
      const stored = await uploadFile(
        file,
        `companies/${companyData.id}/landing-page/${objectFileName(file)}`
      );
      const downloadUrl = stored.url;
      setStorefrontBackgroundImageUrl(downloadUrl);
      await persistLandingPageSettings(customPages, 'Image uploaded successfully!', {
        storefrontBackgroundImageUrl: downloadUrl
      });
    } catch (uploadError) {
      console.error('Error uploading storefront background:', uploadError);
      showToast('Failed to upload storefront background', 'error');
    } finally {
      setBackgroundUploading(false);
    }
  };

  const handleRemoveStorefrontBackground = () => {
    setStorefrontBackgroundImageUrl('');
  };

  const handlePageFieldChange = (pageId, field, value) => {
    setCustomPages((currentPages) => currentPages.map((page) => {
      if (page.id !== pageId) {
        return page;
      }

      if (field === 'slug') {
        return {
          ...page,
          slug: normalizeLandingPageSlug(value, page.title || 'page')
        };
      }

      if (field === 'title') {
        if (page.type === LANDING_PAGE_TYPES.CUSTOM) {
          const currentDefaultSlug = normalizeLandingPageSlug(page.title || 'page');
          const shouldSyncSlug = !page.slug || page.slug === currentDefaultSlug;

          return {
            ...page,
            title: value,
            slug: shouldSyncSlug ? normalizeLandingPageSlug(value, 'page') : page.slug,
            navLabel: value
          };
        }

        return {
          ...page,
          title: value,
          navLabel: value
        };
      }

      return {
        ...page,
        [field]: value
      };
    }));
  };

  const handleContactFieldChange = (pageId, field, value) => {
    setCustomPages((currentPages) => currentPages.map((page) => {
      if (page.id !== pageId) {
        return page;
      }

      return {
        ...page,
        contactFields: {
          ...(page.contactFields || {}),
          [field]: value
        }
      };
    }));
  };

  const handleAddPage = () => {
    const count = customPages.length;
    const nextPage = createLandingCustomPage(`Page ${count + 1}`);
    setCustomPages((currentPages) => [...currentPages, nextPage]);
    setSelectedPageId(nextPage.id);
    setPreviewPageId(nextPage.id);
  };

  const handleAddContactPage = () => {
    if (customPages.some((page) => page.type === LANDING_PAGE_TYPES.CONTACT)) {
      return;
    }

    const nextPage = createLandingContactPage();
    setCustomPages((currentPages) => [...currentPages, nextPage]);
    setSelectedPageId(nextPage.id);
    setPreviewPageId(nextPage.id);
  };

  const persistLandingPageSettings = async (
    nextCustomPages = customPages,
    nextSuccessMessage = 'Settings saved successfully!',
    overrides = {}
  ) => {
    if (!companyData?.id) {
      return false;
    }

    setLoading(true);

    try {
      if (!HEX_COLOR_REGEX.test(colors.theme) || (customThemeHex.trim() && !HEX_COLOR_REGEX.test(customThemeHex.trim()))) {
        throw new Error('Invalid color format. Please use a hex color like #1976d2');
      }

      const currentSavedSlug = getCompanySlug(loadedCompanyData || companyData);
      const trimmedSlug = (resolvedSlug || '').trim();
      let slugToPersist = currentSavedSlug;

      if (trimmedSlug) {
        if (trimmedSlug !== currentSavedSlug) {
          const slugValidation = await ensurePortalSlugCanBeSaved(trimmedSlug, companyData.id, {
            companyData: loadedCompanyData || companyData,
            savedSlug: currentSavedSlug
          });
          if (!slugValidation.ok) {
            throw new Error(slugValidation.error);
          }
          slugToPersist = slugValidation.normalizedSlug;
        } else {
          slugToPersist = trimmedSlug;
        }
      }

      const sharedSlugUpdate = slugToPersist
        ? buildSharedSlugUpdate(companyData, slugToPersist)
        : null;
      const isSlugChange = Boolean(slugToPersist && slugToPersist !== currentSavedSlug);
      const sanitizedSettings = sanitizeLandingPageSettingsForSave({
        themeColor: colors.theme,
        storefrontBackgroundImageUrl: overrides.storefrontBackgroundImageUrl ?? storefrontBackgroundImageUrl,
        storefrontBackgroundOverlayOpacity,
        serviceListingStyle,
        navigation: navigationSettings,
        socialLinks,
        homePage,
        customPages: nextCustomPages
      }, loadedCompanyData || companyData);

      const company = await patchCompany({
        companyId: companyData.id,
        company: {
          ...(sharedSlugUpdate
            ? {
                portalSettings: {
                  ...sharedSlugUpdate.portalSettings,
                  ...(isSlugChange ? { customUrlChangedAt: new Date().toISOString() } : {}),
                },
              }
            : {}),
          landingPageSettings: {
            ...sanitizedSettings,
            ...(slugToPersist ? { customUrl: slugToPersist } : {}),
            updatedAt: new Date().toISOString(),
          },
        },
      });
      updateCompanyData?.({ ...companyData, ...company });

      setCustomPages(sanitizedSettings.customPages);
      setHomePage(sanitizedSettings.homePage);
      setNavigationSettings(sanitizedSettings.navigation);
      setSocialLinks(sanitizedSettings.socialLinks);
      setStorefrontBackgroundImageUrl(sanitizedSettings.storefrontBackgroundImageUrl || '');
      setStorefrontBackgroundOverlayOpacity(sanitizedSettings.storefrontBackgroundOverlayOpacity ?? 0.1);
      setLoadedCompanyData((currentCompanyData) => ({
        ...(currentCompanyData || companyData || {}),
        landingPageSettings: {
          ...sanitizedSettings,
          ...(slugToPersist ? { customUrl: slugToPersist } : {})
        }
      }));
      showToast(nextSuccessMessage, 'success');
      return true;
    } catch (err) {
      console.error('Error saving landing page settings:', err);
      showToast(err.message || 'Failed to save settings', 'error');
      return false;
    } finally {
      setLoading(false);
    }
  };

  const handleDeletePage = async (pageId) => {
    const page = customPages.find((entry) => entry.id === pageId);
    if (!page) {
      return;
    }

    if (!window.confirm(`Delete "${page.title}"? This page will be removed from your website.`)) {
      return;
    }

    const nextPages = customPages.filter((entry) => entry.id !== pageId);
    setCustomPages(nextPages);
    setSelectedPageId(nextPages[0]?.id || '');
    setPreviewPageId((currentPreviewPageId) => (
      currentPreviewPageId === pageId ? 'home' : currentPreviewPageId
    ));
    await persistLandingPageSettings(nextPages, 'Page deleted successfully!');
  };

  const handleSave = async () => {
    await persistLandingPageSettings(customPages, 'Settings saved successfully!');
  };

  useBeforeUnload(
    React.useCallback((event) => {
      if (!hasUnsavedChanges) {
        return;
      }

      event.preventDefault();
      event.returnValue = '';
    }, [hasUnsavedChanges])
  );

  useEffect(() => {
    currentUrlRef.current = `${window.location.pathname}${window.location.search}${window.location.hash}`;
  }, [hasUnsavedChanges]);

  useEffect(() => {
    if (!hasUnsavedChanges) {
      return undefined;
    }

    const confirmNavigation = () => window.confirm('You have unsaved changes. Save them before leaving this page?');
    const { pushState, replaceState } = window.history;

    const resolvePath = (url) => {
      if (url == null) {
        return `${window.location.pathname}${window.location.search}${window.location.hash}`;
      }

      const resolvedUrl = new URL(String(url), window.location.origin);
      return `${resolvedUrl.pathname}${resolvedUrl.search}${resolvedUrl.hash}`;
    };

    const interceptHistoryChange = (originalMethod) => function patchedHistoryMethod(state, unused, url) {
      const nextPath = resolvePath(url);
      if (nextPath === currentUrlRef.current) {
        return originalMethod.apply(window.history, [state, unused, url]);
      }

      if (!confirmNavigation()) {
        return undefined;
      }

      currentUrlRef.current = nextPath;
      return originalMethod.apply(window.history, [state, unused, url]);
    };

    const handlePopState = () => {
      const nextPath = `${window.location.pathname}${window.location.search}${window.location.hash}`;
      if (nextPath === currentUrlRef.current) {
        return;
      }

      if (confirmNavigation()) {
        currentUrlRef.current = nextPath;
        return;
      }

      pushState.call(window.history, window.history.state, '', currentUrlRef.current);
    };

    window.history.pushState = interceptHistoryChange(pushState);
    window.history.replaceState = interceptHistoryChange(replaceState);
    window.addEventListener('popstate', handlePopState);

    return () => {
      window.history.pushState = pushState;
      window.history.replaceState = replaceState;
      window.removeEventListener('popstate', handlePopState);
    };
  }, [hasUnsavedChanges]);

  return (
    <div className="ce-shell">
      {!embedded && (
        <div className="ce-section-header">
          <h1 className="ce-title" style={{ fontSize: '1.7rem', marginBottom: '8px' }}>Website Customization</h1>
          <p className="ce-section-description">
            Edit the storefront, check the live preview, and manage extra pages from one workspace.
          </p>
        </div>
      )}

      <section className={`ce-preview${isMobilePreview ? ' ce-preview--mobile-open' : ''}`} style={previewStyles}>
        <div className="ce-preview__header">
          <h2 className="ce-subsection-title" style={{ margin: 0 }}>
            Live Preview
          </h2>
          {!isMobilePreview && (
            <button
              type="button"
              className="ce-preview__toggle"
              onClick={() => setPreviewExpanded((currentValue) => !currentValue)}
            >
              {previewExpanded ? 'Hide preview' : 'Show preview'}
            </button>
          )}
        </div>

        {isMobilePreview ? (
          <a
            href={livePreviewUrl || undefined}
            target="_blank"
            rel="noopener noreferrer"
            className={`ce-button ce-button--primary ce-preview-mobile__open${!livePreviewUrl ? ' is-disabled' : ''}`}
            aria-disabled={!livePreviewUrl}
            onClick={(event) => {
              if (!livePreviewUrl) {
                event.preventDefault();
              }
            }}
          >
            Open preview
          </a>
        ) : previewExpanded && (
          <StorefrontBuilderPreview
            companyData={previewCompanyData}
            companyUrl={resolvedSlug || 'preview'}
            previewPageId={previewPageId}
            pages={previewPages}
            onPreviewPageChange={setPreviewPageId}
          />
        )}
      </section>


      <div style={{ display: 'grid', gap: '16px' }}>
            <section className="ce-card ce-subcard">
              <h3 className="ce-subsection-title" style={{ marginBottom: '16px' }}>Theme Color</h3>
              <div className="ce-form-item">
                <div className="ce-swatch-picker" role="radiogroup" aria-label="Landing page theme color">
                  {THEME_COLOR_OPTIONS.map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      className={`ce-swatch-option${colors.theme === option.value ? ' is-active' : ''}`}
                      onClick={() => handleThemeColorChange(option.value)}
                      role="radio"
                      aria-checked={colors.theme === option.value}
                      aria-label={option.label}
                    >
                      <span className="ce-swatch-option__color" style={{ background: option.value }} aria-hidden="true" />
                      <span className="ce-swatch-option__label">{option.label}</span>
                    </button>
                  ))}
                </div>
                <div className="ce-form-item" style={{ marginTop: '14px' }}>
                  <label className="ce-label" htmlFor="landing-theme-color">Custom Hex</label>
                  <div className="ce-custom-hex-row">
                    <span
                      className="ce-custom-hex-preview"
                      style={{
                        background: HEX_COLOR_REGEX.test(customThemeHex.trim())
                          ? customThemeHex.trim()
                          : colors.theme
                      }}
                      aria-hidden="true"
                    />
                    <input
                      id="landing-theme-color"
                      className="ce-field"
                      value={customThemeHex}
                      onChange={(e) => handleCustomThemeHexChange(e.target.value)}
                      placeholder={DEFAULT_THEME_COLOR}
                      autoComplete="off"
                    />
                  </div>
                </div>
              </div>
            </section>

            <section className="ce-card ce-subcard">
              <h3 className="ce-subsection-title" style={{ marginBottom: '16px' }}>Layout</h3>
              <div className="ce-form-item">
                <div className="ce-layout-picker" role="radiogroup" aria-label="Homepage services layout">
                  <button
                    type="button"
                    className={`ce-layout-option${serviceListingStyle === SERVICE_LISTING_STYLE_OPTIONS.CARDS ? ' is-active' : ''}`}
                    onClick={() => setServiceListingStyle(SERVICE_LISTING_STYLE_OPTIONS.CARDS)}
                    role="radio"
                    aria-checked={serviceListingStyle === SERVICE_LISTING_STYLE_OPTIONS.CARDS}
                  >
                    <div className="ce-layout-option__preview ce-layout-option__preview--cards" aria-hidden="true">
                      <span className="ce-layout-option__mini-card" />
                      <span className="ce-layout-option__mini-card" />
                      <span className="ce-layout-option__mini-card" />
                    </div>
                    <div className="ce-layout-option__body">
                      <span className="ce-layout-option__title">Cards</span>
                      <span className="ce-layout-option__text">Show services as clean storefront cards.</span>
                    </div>
                  </button>

                  <button
                    type="button"
                    className={`ce-layout-option${serviceListingStyle === SERVICE_LISTING_STYLE_OPTIONS.COMPACT_ROWS ? ' is-active' : ''}`}
                    onClick={() => setServiceListingStyle(SERVICE_LISTING_STYLE_OPTIONS.COMPACT_ROWS)}
                    role="radio"
                    aria-checked={serviceListingStyle === SERVICE_LISTING_STYLE_OPTIONS.COMPACT_ROWS}
                  >
                    <div className="ce-layout-option__preview ce-layout-option__preview--rows" aria-hidden="true">
                      <span className="ce-layout-option__mini-row" />
                      <span className="ce-layout-option__mini-row" />
                      <span className="ce-layout-option__mini-row" />
                    </div>
                    <div className="ce-layout-option__body">
                      <span className="ce-layout-option__title">Compact rows</span>
                      <span className="ce-layout-option__text">Keep the storefront lighter and easier to scan.</span>
                    </div>
                  </button>
                </div>
              </div>
            </section>

            <section className="ce-card ce-subcard">
              <h3 className="ce-subsection-title" style={{ marginBottom: '8px' }}>Storefront Background</h3>
              <p className="ce-helper" style={{ margin: '0 0 16px' }}>
                Upload a photo of the storefront or space to use as the website background.
              </p>
              <div style={{ display: 'grid', gap: '14px' }}>
                <label
                  className={`ce-background-upload-zone ce-card ce-subcard${storefrontBackgroundImageUrl ? ' has-image' : ''}`}
                  style={{
                    cursor: backgroundUploading ? 'progress' : 'pointer',
                    ...(storefrontBackgroundImageUrl ? {
                      backgroundImage: `linear-gradient(180deg, rgba(255,255,255,${storefrontBackgroundOverlayOpacity}), rgba(255,255,255,${storefrontBackgroundOverlayOpacity})), url("${storefrontBackgroundImageUrl}")`
                    } : {})
                  }}
                >
                  <input
                    type="file"
                    accept="image/*"
                    disabled={backgroundUploading}
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        handleStorefrontBackgroundUpload(file);
                      }
                      e.target.value = '';
                    }}
                    style={{ display: 'none' }}
                  />
                  <div className={`ce-background-upload-label${storefrontBackgroundImageUrl ? ' ce-background-upload-label--on-image' : ''}`}>
                    <strong>{backgroundUploading ? 'Uploading...' : storefrontBackgroundImageUrl ? 'Replace Background' : 'Upload Background'}</strong>
                    <span className="ce-helper">JPG or PNG recommended</span>
                  </div>
                </label>
                <div className="ce-inline-actions">
                  <button
                    type="button"
                    className="ce-button ce-button--secondary"
                    onClick={handleRemoveStorefrontBackground}
                    disabled={!storefrontBackgroundImageUrl || backgroundUploading}
                  >
                    Remove Background
                  </button>
                </div>
                <div className="ce-form-item">
                  <label className="ce-label" htmlFor="landing-background-opacity">
                    Background Overlay
                  </label>
                  <input
                    id="landing-background-opacity"
                    type="range"
                    min="0"
                    max="0.9"
                    step="0.05"
                    value={storefrontBackgroundOverlayOpacity}
                    onChange={(e) => setStorefrontBackgroundOverlayOpacity(Number(e.target.value))}
                    disabled={backgroundUploading}
                  />
                  <p className="ce-helper" style={{ margin: 0 }}>
                    {Math.round(storefrontBackgroundOverlayOpacity * 100)}% overlay
                  </p>
                </div>
              </div>
            </section>

            <section className="ce-card ce-subcard">
              <h3 className="ce-subsection-title" style={{ marginBottom: '8px' }}>Social Media Links</h3>
              <p className="ce-helper" style={{ margin: '0 0 16px' }}>
                Paste the social links you want to show in the small header under your main website navigation.
              </p>
              <div className="ce-form-grid">
                {LANDING_PAGE_SOCIAL_PLATFORMS.map((platform) => (
                  <div className="ce-form-item" key={platform.key}>
                    <label className="ce-label" htmlFor={`landing-social-${platform.key}`}>{platform.label}</label>
                    <input
                      id={`landing-social-${platform.key}`}
                      className="ce-field"
                      value={socialLinks[platform.key] || ''}
                      onChange={(e) => handleSocialLinkChange(platform.key, e.target.value)}
                      placeholder={`https://${platform.key}.com/your-handle`}
                      autoComplete="off"
                    />
                  </div>
                ))}
              </div>
            </section>

      </div>

      <section className="ce-card ce-subcard">
        <div className="ce-pages-workspace-header">
          <div className="ce-pages-workspace-header__intro">
            <h2 className="ce-subsection-title" style={{ marginBottom: '6px' }}>Pages Workspace</h2>
            <p className="ce-helper" style={{ margin: 0 }}>
              Add pages only if you need them, then select one from the left to edit it in place.
            </p>
          </div>
          <div className="ce-pages-workspace-header__actions">
            <button type="button" className="ce-button ce-button--secondary" onClick={handleAddPage}>
              Add Page
            </button>
            {!hasContactPage && (
              <button
                type="button"
                className="ce-button ce-button--secondary"
                onClick={handleAddContactPage}
              >
                Add Contact Page
              </button>
            )}
          </div>
        </div>

        <div className="ce-pages-workspace">
          <div style={{ display: 'grid', gap: '10px', alignContent: 'start' }}>
            {previewPages.length > 0 ? (
              previewPages.map((page) => (
                <button
                  key={page.id}
                  type="button"
                  onClick={() => {
                    setSelectedPageId(page.id);
                    setPreviewPageId(page.id);
                  }}
                  className={`ce-card ce-subcard ce-pages-workspace-page${selectedPage?.id === page.id ? ' is-selected' : ''}`}
                  style={{
                    borderColor: selectedPage?.id === page.id ? colors.theme : undefined,
                    boxShadow: selectedPage?.id === page.id ? `0 0 0 1px ${colors.theme}` : undefined
                  }}
                >
                  <div className="ce-pages-workspace-page__row">
                    <strong className="ce-pages-workspace-page__title">{page.title}</strong>
                    <span className="ce-preview__compact-pill">{page.isVisible !== false ? 'Visible' : 'Hidden'}</span>
                  </div>
                  <div className="ce-pages-workspace-page__type">
                    {pageTypeLabels[page.type]}
                  </div>
                </button>
              ))
            ) : (
              <div className="ce-card ce-subcard" style={{ padding: '18px' }}>
                <p className="ce-helper" style={{ margin: 0 }}>
                  No pages yet. Add a standard page or a contact page when you want one.
                </p>
              </div>
            )}
          </div>

          <div className="ce-card ce-subcard">
            {selectedPage ? (
              <div className="ce-pages-workspace-editor">
                <div className="ce-pages-workspace-editor-header">
                  <div className="ce-pages-workspace-editor-header__intro">
                    <h3 className="ce-subsection-title" style={{ marginBottom: '6px' }}>{selectedPage.title}</h3>
                    <p className="ce-helper" style={{ margin: 0 }}>{pageTypeLabels[selectedPage.type]}</p>
                  </div>
                  {(selectedPage.type === LANDING_PAGE_TYPES.CUSTOM
                    || selectedPage.type === LANDING_PAGE_TYPES.CONTACT) && (
                    <button
                      type="button"
                      className="ce-button ce-button--secondary ce-pages-workspace-editor-header__delete"
                      onClick={() => handleDeletePage(selectedPage.id)}
                      style={{
                        borderColor: 'rgba(220, 38, 38, 0.2)',
                        color: '#b91c1c'
                      }}
                    >
                      Delete Page
                    </button>
                  )}
                </div>

                <div className="ce-form-item">
                  <label className="ce-label" htmlFor="landing-page-title">Page Title</label>
                  <input
                    id="landing-page-title"
                    className="ce-field"
                    value={selectedPage.title}
                    onChange={(e) => handlePageFieldChange(selectedPage.id, 'title', e.target.value)}
                  />
                </div>

                <label className="ce-pages-workspace-visibility">
                  <input
                    type="checkbox"
                    checked={selectedPage.isVisible !== false}
                    onChange={() => handlePageFieldChange(selectedPage.id, 'isVisible', selectedPage.isVisible === false)}
                  />
                  <span>Show page</span>
                </label>

                {selectedPage.type === LANDING_PAGE_TYPES.CONTACT ? (
                  <div style={{ display: 'grid', gap: '16px' }}>
                    <div className="ce-form-item">
                      <label className="ce-label" htmlFor="landing-contact-phone">Phone Number</label>
                      <input
                        id="landing-contact-phone"
                        className="ce-field"
                        value={selectedPage.contactFields?.phone || ''}
                        onChange={(e) => handleContactFieldChange(selectedPage.id, 'phone', e.target.value)}
                        placeholder={previewPhone || '(555) 123-4567'}
                      />
                    </div>
                    <div className="ce-form-item">
                      <label className="ce-label" htmlFor="landing-contact-email">Email Address</label>
                      <input
                        id="landing-contact-email"
                        className="ce-field"
                        value={selectedPage.contactFields?.email || ''}
                        onChange={(e) => handleContactFieldChange(selectedPage.id, 'email', e.target.value)}
                        placeholder={previewEmail || 'contact@example.com'}
                      />
                    </div>
                    <div className="ce-form-item">
                      <label className="ce-label" htmlFor="landing-contact-address">Address</label>
                      <input
                        id="landing-contact-address"
                        className="ce-field"
                        value={selectedPage.contactFields?.address || ''}
                        onChange={(e) => handleContactFieldChange(selectedPage.id, 'address', e.target.value)}
                        placeholder={previewAddress || '123 Main St, City, State'}
                      />
                    </div>
                  </div>
                ) : (
                  <div className="ce-form-item">
                    <label className="ce-label" htmlFor="landing-page-content">Page Content</label>
                    <textarea
                      id="landing-page-content"
                      className="ce-textarea"
                      rows={10}
                      value={selectedPage.content}
                      onChange={(e) => handlePageFieldChange(selectedPage.id, 'content', e.target.value)}
                      placeholder="Add the content customers should see on this page."
                    />
                  </div>
                )}

              </div>
            ) : (
              <p className="ce-helper" style={{ margin: 0 }}>Add a page to start editing storefront content.</p>
            )}
          </div>
        </div>
      </section>

      <div className="ce-actions" style={{ justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
        <div className="ce-helper" style={{ margin: 0 }}>
          {hasUnsavedChanges
            ? 'You have unsaved changes. Save before leaving this page.'
            : 'All changes saved.'}
        </div>
        <button type="button" className="ce-button ce-button--primary" onClick={handleSave} disabled={loading || backgroundUploading || !hasUnsavedChanges}>
          {loading ? 'Saving...' : 'Save Changes'}
        </button>
      </div>
    </div>
  );
};

export default LandingPageSettings;

